import { Link } from 'react-router';
import { Bouton } from '../shared/ui/Bouton';
import { Paragraphe, Section } from '../shared/ui/Section';
import { Titre } from '../shared/ui/Titre';

/**
 * **PAGE 3 DES HUIT — celle qui s'adresse aux TUTEURS**, F-69.
 *
 * **ELLE PARLE À DEUX PERSONNES DIFFÉRENTES SOUS UN SEUL MOT, ET IL FAUT LE SAVOIR AVANT DE
 * L'ÉCRIRE.** Le corpus emploie « tuteur » pour **un rôle du produit** — celui qui tient un
 * cours, provisionné par une structure — et le cadrage nomme parmi les clientes à venir des
 * « **tuteurs indépendants sans lien avec le groupe** », qui sont des **clientes**, donc
 * l'équivalent d'une structure d'une seule personne. `CDC-6` §5.3 s'appuie explicitement sur
 * ce second sens : « une structure cliente **peut donc être une personne** ».
 *
 * **Cette page s'adresse au second**, puisque `CDC-6` §1 dit que le site « présente le
 * produit, nomme ses clientes » — un tuteur employé par une structure, lui, reçoit son accès
 * de son employeur et n'a pas de décision d'achat à prendre. La page ne le laisse pas de
 * côté pour autant : elle dit ce qu'il trouve, et par qui son accès arrive.
 *
 * **AUCUN TARIF, AUCUNE INSCRIPTION** — F-69 et §12. Un tuteur indépendant qui lit cette page
 * ne peut pas ouvrir un compte depuis le site : « aucune inscription publique, aucun bouton de
 * création de compte ». **C'est une règle et non un manque**, et la page le dit plutôt que de
 * laisser chercher le bouton.
 */
export function PageTuteurs() {
  return (
    <>
      <Section largeur="etroite">
        <Titre niveau={1}>Pour un tuteur</Titre>
        <Paragraphe>
          Vous donnez des cours à distance et vous voulez écrire à la main
          devant votre élève, pas partager un écran. Swype est un tableau au
          stylet que vous tenez à deux, avec la voix et l’image quand elles
          servent.
        </Paragraphe>
      </Section>

      <Section titre="Ce que vous trouvez">
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Titre niveau={3} taille="18">
              Le cours tient dans un écran
            </Titre>
            <Paragraphe>
              Le tableau prend toute la place. Pas de barre à traverser, pas
              d’écran à changer en cours de route : on y travaille, on n’y
              navigue pas.
            </Paragraphe>
          </div>
          <div className="flex flex-col gap-2">
            <Titre niveau={3} taille="18">
              Votre élève entre par un lien
            </Titre>
            <Paragraphe>
              Vous lui envoyez un lien qui l’amène directement dans le cours. Il
              n’a ni compte à créer, ni mot de passe à retenir pour cette
              fois-là.
            </Paragraphe>
          </div>
          <div className="flex flex-col gap-2">
            <Titre niveau={3} taille="18">
              Ce qui a été écrit se relit
            </Titre>
            <Paragraphe>
              Après le cours, le tableau reste consultable pendant une durée que
              la structure règle. Vos élèves peuvent y revenir.
            </Paragraphe>
          </div>
          <div className="flex flex-col gap-2">
            <Titre niveau={3} taille="18">
              Vos heures sont relevées
            </Titre>
            <Paragraphe>
              Vous n’avez rien à déclarer : l’entrée et la sortie de chaque
              participant sont mesurées, et la durée en découle.
            </Paragraphe>
          </div>
        </div>
      </Section>

      <Section largeur="etroite" titre="Un stylet, un écran assez large">
        <Paragraphe>
          Le tableau se tient au stylet, en paysage, sur un écran d’au moins
          1024 points de large. Un téléphone lit ce site sans difficulté, mais
          il ne tient pas un cours : c’est une contrainte du tracé, pas un choix
          d’affichage.
        </Paragraphe>
      </Section>

      <Section largeur="etroite" titre="Comment on commence">
        <Paragraphe>
          Il n’y a pas d’inscription sur ce site, et ce n’est pas un oubli :
          chaque accès est ouvert par la structure qui l’utilise. Si vous
          travaillez seul, écrivez-nous et nous ouvrirons le vôtre.
        </Paragraphe>
        <div className="flex flex-wrap gap-3">
          <Link to="/contact">
            <Bouton>Nous écrire</Bouton>
          </Link>
          <Link to="/produit">
            <Bouton variante="faible">Le produit en détail</Bouton>
          </Link>
        </div>
      </Section>
    </>
  );
}
